import { LegacyTokens } from "../../../../theme/b13Tokens";
import { EditorPhase, makeEmptyPhase, normalisePhase } from "./WorkflowEditorBody";
import { objectiveByKey } from "./workflowObjectives";

/**
 * T63 Part O §19 / §L — HiRasmus-inspired standard workflow structure.
 *
 * Every new Percent Correct workflow initialises with Probe →
 * Acquisition → Mastered (plus the Waiting card rendered separately by
 * WorkflowEditorBody). "Unnamed phase" is never the first thing the
 * operator sees on the create route.
 *
 * Only Percent Correct is fully implemented today; Frequency / Rate /
 * Duration / Manual objectives are disabled at the chooser
 * (ObjectiveChooserDialog) so they never reach this factory, but if a
 * legacy URL param somehow slips through we still return a sensible
 * three-phase skeleton so the editor is usable.
 */
export function createStandardWorkflowForObjective(
  objectiveKey: string | null | undefined,
): EditorPhase[] {
  const objective = objectiveByKey(objectiveKey ?? null);
  const isDecrease = objective?.direction === "decrease";

  // T63 Part O §R — Probe defaults match HiRasmus:
  //   If mastered → Target Status = Closed  (mastery ends progression)
  //   If not mastered → Acquisition
  //   Header therefore reads "Probe → Closed", never "Probe → Mastered
  //   → Closed" (getEffectivePhaseDestination picks the target status).
  //   No free-text advancement/regression rule seeded — those were
  //   operator-facing junk (T63 Part O §F).
  const probe: Omit<EditorPhase, "orderNumber" | "_dndId"> = {
    phaseName: "Probe",
    colour: LegacyTokens.amber,
    icon: "AssessmentOutlined",
    description: "",
    entryRule: "",
    advancementRule: "",
    regressionRule: "",
    transitionsTo: "",
    masteredTargetStatus: "closed",
    notMasteredTransitionsTo: "Acquisition",
    minPercentCorrect: 100,
    acrossSessions: 1,
    minTrialsPerSession: 3,
    promptRequirement: "independent_only",
    manualApprovalRequired: false,
    isStarting: true,
    isTerminal: false,
    isActive: true,
    countsTowardAutoOpenLimit: true,
  };
  // Reduce-family probe: mastery still resolves to Closed by target
  // status; the criterion direction is handled at evaluation time.
  void isDecrease;

  // T63 Part O §S — Acquisition: on mastery → Mastered.
  const acquisition: Omit<EditorPhase, "orderNumber" | "_dndId"> = {
    phaseName: "Acquisition",
    colour: LegacyTokens.blue,
    icon: "Layers",
    description: "",
    entryRule: "",
    advancementRule: "",
    regressionRule: "",
    transitionsTo: "Mastered",
    masteredTargetStatus: "",
    notMasteredTransitionsTo: "",
    minPercentCorrect: 80,
    acrossSessions: 3,
    minTrialsPerSession: 1,
    promptRequirement: "any",
    manualApprovalRequired: false,
    isStarting: false,
    isTerminal: false,
    isActive: true,
    countsTowardAutoOpenLimit: true,
  };

  // T63 Part O §T — Mastered acts as HiRasmus's maintenance phase.
  //   Maintenance criteria: 100% / 1 trial (interval frequency lives
  //   in phase_intervals — MaintenanceIntervalRows, added post-save).
  //   If maintenance criteria are not met → Acquisition fallback.
  //   If final closure is configured → Closed via masteredTargetStatus.
  const mastered: Omit<EditorPhase, "orderNumber" | "_dndId"> = {
    phaseName: "Mastered",
    colour: LegacyTokens.emerald,
    icon: "EmojiEvents",
    description: "",
    entryRule: "",
    advancementRule: "",
    regressionRule: "",
    transitionsTo: "",
    masteredTargetStatus: "closed",
    notMasteredTransitionsTo: "Acquisition",
    minPercentCorrect: 100,
    acrossSessions: 1,
    minTrialsPerSession: 1,
    promptRequirement: "independent_only",
    manualApprovalRequired: false,
    isStarting: false,
    isTerminal: true,
    isActive: true,
    countsTowardAutoOpenLimit: false,
  };

  return [probe, acquisition, mastered].map((p, i) =>
    normalisePhase({ ...p, orderNumber: i }, i),
  );
}

/** Fallback for any edge case where the factory returns an empty list. */
export function createStandardOrEmpty(objectiveKey: string | null | undefined): EditorPhase[] {
  const std = createStandardWorkflowForObjective(objectiveKey);
  return std.length ? std : [makeEmptyPhase(0)];
}
